// One phone bottom sheet for the kit's pickers (DES1: ST-07, LB-07/08, Team pickers), so each
// caller stops carrying its own copy of the same CSS. Desktop keeps the Radix Menu; a caller
// shows this at ≤ 640 px.
//
// BottomSheet is the kit Dialog pinned to the bottom edge: scrim, the 36 × 4 handle, a title,
// full width, at most 85% of the screen, safe-area padding. The Dialog traps focus, closes on
// Escape and on a tap outside, and focus returns to the trigger (also where a tap did not focus
// it, as on iOS Safari). SheetRadioList is the one-choice list inside it.
import {
  useRef,
  type ComponentProps,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { CheckIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

export function BottomSheet({
  open,
  onOpenChange,
  title,
  returnFocus,
  testId,
  contentProps,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The sheet's title: the picker's own label ("Kind", "Access", "Time zone"). */
  title: ReactNode;
  /** The trigger; focus goes back to it when the sheet closes. */
  returnFocus?: RefObject<HTMLElement | null>;
  testId?: string;
  /**
   * For a host that must keep the sheet inside its own layer (the inspector, IN-14): where it
   * mounts and its own ref, style, class and Escape/auto-focus handling. The sheet's look and
   * focus return stay the kit's; a pickers' caller passes nothing.
   */
  contentProps?: Pick<
    ComponentProps<typeof DialogContent>,
    'ref' | 'style' | 'className' | 'portalContainer' | 'onEscapeKeyDown' | 'onOpenAutoFocus'
  >;
  children: ReactNode;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        {...contentProps}
        data-slot="bottom-sheet"
        data-testid={testId}
        className={cn(
          'top-auto bottom-0 left-0 flex max-h-[85dvh] max-w-none translate-x-0 translate-y-0 flex-col gap-3 rounded-b-none p-4 pt-2 pb-[max(1rem,env(safe-area-inset-bottom))] sm:max-w-none',
          contentProps?.className,
        )}
        aria-describedby={undefined}
        onCloseAutoFocus={(event) => {
          if (!returnFocus) return;
          event.preventDefault();
          returnFocus.current?.focus();
        }}
      >
        {/* The kit sheet handle (the Requirements sheet's 36 × 4, muted). Decorative: no drag. */}
        <span
          aria-hidden="true"
          data-slot="sheet-handle"
          className="bg-muted-foreground/60 mx-auto -mb-1 block h-1 w-9 shrink-0 rounded-xs"
        />
        <DialogTitle className="text-base">{title}</DialogTitle>
        <div className="flex min-h-0 flex-1 flex-col gap-3">{children}</div>
      </DialogContent>
    </Dialog>
  );
}

export interface SheetChoice {
  value: string;
  label: string;
  /** A muted second line. */
  description?: string;
  disabled?: boolean;
}

/**
 * One choice from a short list: 44 px rows, ✓ on the chosen one, disabled rows muted. ↑/↓ (and
 * Home/End) move focus only; Enter, Space or a tap chooses, so moving never closes the sheet.
 */
export function SheetRadioList({
  label,
  choices,
  value,
  onChoose,
}: {
  /** The group's accessible name (usually the sheet title). */
  label: string;
  choices: readonly SheetChoice[];
  value: string;
  onChoose: (value: string) => void;
}) {
  const group = useRef<HTMLDivElement>(null);
  const enabled = choices.filter((choice) => !choice.disabled);
  // One tab stop: the chosen row, or the first that can be chosen.
  const stop = enabled.some((choice) => choice.value === value) ? value : enabled[0]?.value;
  const move = (event: KeyboardEvent<HTMLDivElement>) => {
    const rows = [
      ...(group.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]:not(:disabled)') ??
        []),
    ];
    const at = rows.indexOf(document.activeElement as HTMLButtonElement);
    const next =
      event.key === 'ArrowDown'
        ? Math.min(rows.length - 1, at + 1)
        : event.key === 'ArrowUp'
          ? Math.max(0, at - 1)
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? rows.length - 1
              : null;
    if (next === null) return;
    event.preventDefault();
    rows[next]?.focus();
  };
  return (
    <div
      ref={group}
      role="radiogroup"
      aria-label={label}
      className="-mx-1 min-h-0 overflow-y-auto overscroll-contain"
      onKeyDown={move}
    >
      {choices.map((choice) => {
        const checked = choice.value === value;
        return (
          <button
            key={choice.value}
            type="button"
            role="radio"
            aria-checked={checked}
            disabled={choice.disabled}
            tabIndex={choice.value === stop ? 0 : -1}
            className="hover:bg-accent focus-visible:bg-accent flex min-h-11 w-full items-center gap-3 rounded-sm px-3 py-2 text-left text-sm outline-none disabled:cursor-default disabled:opacity-50"
            onClick={() => onChoose(choice.value)}
          >
            <span className="min-w-0 flex-1">
              <span className="block">{choice.label}</span>
              {choice.description && (
                <span className="text-muted-foreground block text-xs">{choice.description}</span>
              )}
            </span>
            {checked && <CheckIcon aria-hidden className="size-4 shrink-0" />}
          </button>
        );
      })}
    </div>
  );
}
